import React, { useEffect, useState } from 'react';
import { Megaphone, RefreshCw, Save } from 'lucide-react';
import adminCampaignService, { AdminCampaign } from '../../services/api/adminCampaignService';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyInput,
  TaskyLabel,
  TaskySpinner,
  TaskySwitch,
} from '../tasky';

/** 画面上で編集中の値（保存するまでサーバーには反映しない） */
interface CampaignForm {
  is_enabled: boolean;
  ends_at: string;
}

const toForm = (campaign: AdminCampaign): CampaignForm => ({
  is_enabled: campaign.is_enabled,
  ends_at: campaign.ends_at ?? '',
});

const today = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

/** 0.5 → 「50%OFF」 */
const discountLabel = (rate: number): string => `${Math.round((1 - rate) * 100)}%OFF`;

const CampaignManagementPanel: React.FC = () => {
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [forms, setForms] = useState<Record<string, CampaignForm>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminCampaignService.list();
      const list = response.data.campaigns;
      setCampaigns(list);
      setForms(Object.fromEntries(list.map((campaign) => [campaign.key, toForm(campaign)])));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'キャンペーン設定の取得に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const updateForm = (key: string, patch: Partial<CampaignForm>) => {
    setForms((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleSave = async (campaign: AdminCampaign) => {
    const form = forms[campaign.key];
    if (!form) return;

    if (form.is_enabled && !form.ends_at) {
      setMessage(null);
      setError('キャンペーンをオンにする場合は終了日を指定してください。');
      return;
    }

    try {
      setSavingKey(campaign.key);
      setError(null);
      setMessage(null);
      const response = await adminCampaignService.update(campaign.key, {
        is_enabled: form.is_enabled,
        ends_at: form.ends_at || null,
      });
      const saved = response.data.campaign;
      setCampaigns((prev) => prev.map((item) => (item.key === saved.key ? saved : item)));
      setForms((prev) => ({ ...prev, [saved.key]: toForm(saved) }));
      setMessage(`「${saved.label}」を保存しました。`);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'キャンペーン設定の保存に失敗しました。');
    } finally {
      setSavingKey(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10">
        <TaskySpinner size="md" />
        <span className="text-sm text-gray-600">キャンペーン設定を読み込み中...</span>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      {message && <TaskyAlert tone="success">{message}</TaskyAlert>}
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <div>
        <h2 className="text-base font-semibold text-dark">キャンペーン</h2>
        <p className="mt-1 text-sm text-gray-600">
          消費クレジットの割引を期間限定で有効にします。オンにした場合、指定した終了日いっぱい（23:59）まで割引が適用されます。
        </p>
      </div>

      <div className="space-y-4">
        {campaigns.map((campaign) => {
          const form = forms[campaign.key] ?? toForm(campaign);
          const isSaving = savingKey === campaign.key;

          return (
            <div
              key={campaign.key}
              className="rounded-tasky-md border border-warm-border bg-white p-4 sm:p-5"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Megaphone className="h-4 w-4 text-primary" />
                    <h3 className="text-sm font-semibold text-dark">{campaign.label}</h3>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{campaign.description}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <TaskyBadge variant="secondary">{discountLabel(campaign.discount_rate)}</TaskyBadge>
                  <TaskyBadge variant={campaign.is_active ? 'success' : 'secondary'}>
                    {campaign.is_active ? '適用中' : '停止中'}
                  </TaskyBadge>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-4 border-t border-warm-border pt-4 sm:flex-row sm:items-end">
                <div className="sm:pb-2">
                  <TaskySwitch
                    checked={form.is_enabled}
                    onChange={(event) => updateForm(campaign.key, { is_enabled: event.target.checked })}
                    label={form.is_enabled ? 'オン' : 'オフ'}
                  />
                </div>

                <div className="sm:w-56">
                  <TaskyLabel htmlFor={`ends-at-${campaign.key}`}>終了日（この日まで割引）</TaskyLabel>
                  <TaskyInput
                    id={`ends-at-${campaign.key}`}
                    type="date"
                    min={today()}
                    value={form.ends_at}
                    onChange={(event) => updateForm(campaign.key, { ends_at: event.target.value })}
                  />
                </div>

                <div className="sm:pb-1">
                  <TaskyButton size="sm" onClick={() => handleSave(campaign)} disabled={isSaving}>
                    {isSaving ? <RefreshCw className="animate-spin" /> : <Save />}
                    保存
                  </TaskyButton>
                </div>
              </div>

              {campaign.is_enabled && campaign.ends_at && !campaign.is_active && (
                <p className="mt-3 text-sm text-destructive">
                  終了日（{campaign.ends_at}）を過ぎているため、割引は適用されていません。
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CampaignManagementPanel;
